import {
  faCancel,
  faCheck,
  faClock,
  faClose,
  faHourglassStart,
  faPlay,
  faReplyAll,
  faRoad,
  faThumbsUp,
  faTicket,
  faTruck,
  faUsd,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { translateOrderState } from '@/utils/translate'
import { translateContainerStatus } from '@/containers/logistic/containers/utils/translators'
import { Badge } from '../primitives/Badge'
import type { BadgeProps } from '../primitives/Badge'

/**
 * StatusBadgeWithIcon component for displaying status with FontAwesome icons
 * Supports both order and container status types
 * Designed to be a universal status badge component
 *
 * @example
 * ```tsx
 * // For orders
 * <StatusBadgeWithIcon status="CREATED" statusType="order" />
 *
 * // For containers
 * <StatusBadgeWithIcon status="CREATED" statusType="container" />
 * ```
 */

type StatusType = 'order' | 'container'

interface StatusBadgeWithIconProps {
  /**
   * Status code
   */
  status?: string
  /**
   * Type of status to determine translation and styling
   * @default 'order'
   */
  statusType?: StatusType
  /**
   * Show icon (default: true)
   */
  showIcon?: boolean
  /**
   * Only show icon without text (default: false)
   */
  onlyShowIcon?: boolean
  /**
   * Additional CSS classes
   */
  className?: string
}

const getStatusVariant = (value: string | null): BadgeProps['variant'] => {
  switch (value) {
    case 'ACTIVE':
    case 'COMPLETED':
    case 'DELIVERED':
    case 'PAID':
    case 'BILLED':
    case 'ACCEPTED':
    case 'WAREHOUSE_RECEIVED':
    case 'EMPTY_RETURNED':
      return 'success'

    case 'CLOSED':
      return 'secondary'

    case 'REFUNDED':
      return 'exceptional'

    case 'CANCELLED':
      return 'destructive'

    case 'OVERDUE':
      return 'warning'

    case 'CREATED':
      return 'info'

    case 'IN_PROCESS':
    case 'PROCESSED':
    case 'ORIGIN_RECEIVED':
    case 'DESTINY_ARRIVED':
    case 'BORDER_RELEASED':
      return 'info'

    case 'DELIVERY_ASSIGNED':
    case 'IN_TRANSIT':
    case 'INTERNATIONAL_TRANSIT':
    case 'NATIONAL_TRANSIT':
      return 'warning'

    case 'READY_TO_TRANSPORT':
    case 'READY_TO_EMPTY_RETURN':
      return 'warning'

    default:
      return 'default'
  }
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'ACTIVE':
    case 'COMPLETED':
    case 'DELIVERED':
    case 'WAREHOUSE_RECEIVED':
    case 'EMPTY_RETURNED':
      return faCheck

    case 'CLOSED':
      return faClose

    case 'CANCELLED':
      return faCancel

    case 'OVERDUE':
      return faClock

    case 'IN_PROCESS':
    case 'WAITING':
    case 'ORIGIN_RECEIVED':
    case 'DESTINY_ARRIVED':
    case 'BORDER_RELEASED':
      return faHourglassStart

    case 'PROCESSED':
      return faPlay

    case 'DELIVERY_ASSIGNED':
    case 'READY_TO_TRANSPORT':
    case 'READY_TO_EMPTY_RETURN':
      return faTruck

    case 'ACCEPTED':
      return faThumbsUp

    case 'IN_TRANSIT':
    case 'INTERNATIONAL_TRANSIT':
    case 'NATIONAL_TRANSIT':
      return faRoad

    case 'REFUNDED':
      return faReplyAll

    case 'PAYMENT_PENDING':
      return faTicket

    case 'BILLED':
      return faUsd

    case 'CREATED':
      return faCheck

    default:
      return faCheck
  }
}

export function StatusBadgeWithIcon({
  status,
  statusType = 'order',
  showIcon = true,
  onlyShowIcon,
  className = '',
}: StatusBadgeWithIconProps) {
  if (!status) return <></>

  // Select the appropriate translation function based on statusType
  const getStatusLabel = () => {
    if (statusType === 'container') {
      return translateContainerStatus(status)
    }
    return translateOrderState(status)
  }

  const icon = showIcon ? (
    <FontAwesomeIcon className="h-3 w-3" icon={getStatusIcon(status)} />
  ) : undefined

  if (onlyShowIcon) {
    return (
      <Badge
        className={className}
        icon={icon}
        size="dot"
        variant={getStatusVariant(status)}
      />
    )
  }

  return (
    <Badge
      className={className}
      icon={icon}
      size="compact"
      variant={getStatusVariant(status)}
    >
      {getStatusLabel()}
    </Badge>
  )
}
